import AvatarLegend from "@/partials/avatars/AvatarLegend";
import { useTranslation } from "@vixi/localization";
import { useNavigate } from "@vixi/router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { NoAvatarsFound } from "@/partials/avatars/NoAvatarsFound";
import { useCallback, useEffect, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEditCourseGoBack } from "@/hooks/ui/useEditCourseRoute";
import { RouteOptionsById, router, RouterIds } from "@vixi/router";
import {
  AvatarGridItem,
  avatars_per_course,
  avatars_per_query,
  Button,
  cn,
  EmptyAvatarGridItem,
  FieldLabel,
  FormHeading,
  LoadingScreen,
  PageMainContent,
} from "@vixi/ui";
import api, {
  allAvatarsQueryOptions,
  courseBaseByIdQueryOptions,
  endpoints,
  useBasicEditCourse,
} from "@vixi/api";
import { genArray } from "@vixi/utils";
import { CHARACTER_TYPE, EditBasicCourseRequest, EditCourseRequest, isCharacter } from "@vixi/types";

// Constants
const routeId = "/_app/courses/$courseId/edit-avatars" satisfies RouterIds;
const Route = router.routesById[routeId];

// Validation
const courseAvatarsValidation = z.object({
  selectedAvatars: z
    .array(
      z
        .object({
          id: z.number(),
          url: z.string(),
        })
        .catchall(z.any()),
    )
    .min(1),
});
type AvatarsFormValues = z.infer<typeof courseAvatarsValidation>;

export const EditAvatarsPageOptions: RouteOptionsById<typeof routeId> = {
  component: RouteComponent,
  loader: async ({ context: { queryClient }, params: { courseId } }) => {
    queryClient.ensureQueryData(allAvatarsQueryOptions(1, avatars_per_query));
    queryClient.ensureQueryData(courseBaseByIdQueryOptions(queryClient, courseId));
  },
};

function RouteComponent() {
  useEditCourseGoBack();
  const { t, tNum } = useTranslation();
  const { courseId } = Route.useParams();

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    watch,
    control,
    setValue,
    reset,
    formState: { isDirty, isValid, errors: _errors },
  } = useForm<AvatarsFormValues>({
    defaultValues: {
      selectedAvatars: [],
    },
    resolver: zodResolver(courseAvatarsValidation),
  });

  const selectedAvatars = useMemo(() => {
    const selected = watch("selectedAvatars");
    return genArray((i) => {
      if (i in selected) {
        return selected[i];
      }
      return null;
    }, 4);
  }, [watch("selectedAvatars")]);

  const { append, remove } = useFieldArray({
    name: "selectedAvatars",
    control,
  });

  const selectedCount = watch("selectedAvatars")?.length;
  const { data: courseDetails, isFetching: courseDetailsLoading } = useQuery(
    courseBaseByIdQueryOptions(queryClient, courseId),
  );

  const courseCharacters = courseDetails?.courseCharacters;
  const { data: allAvatars, isSuccess: isAvatarsLoaded } = useQuery(
    allAvatarsQueryOptions(1, avatars_per_query),
  );

  const otherAvatars = useMemo(() => {
    return allAvatars?.items?.filter(
      (avatar) =>
        // Only show avatars (COURSE_CHARACTER), not marketing kit items
        isCharacter(avatar) &&
        !watch("selectedAvatars").some((character) => character && character.id === avatar.id),
    );
  }, [allAvatars, watch("selectedAvatars")]);

  useEffect(() => {
    if (courseCharacters) {
      // Filter to show only avatars (COURSE_CHARACTER), not marketing kit items
      const avatarsOnly = courseCharacters.filter(isCharacter);
      reset({
        selectedAvatars: avatarsOnly,
      });
    }
  }, [courseCharacters, setValue]);

  const { mutateAsync: editCourseBase } = useBasicEditCourse();

  const saveChanges = useCallback(async () => {
    const course =
      courseDetails ??
      (await queryClient.ensureQueryData(courseBaseByIdQueryOptions(queryClient, courseId)));

    const newCourse = {
      courseId: parseInt(courseId) ?? "",
      course: {
        ...course,
        courseCharacters: watch("selectedAvatars").map((avatar) => ({
          ...avatar,
          characterType: avatar.characterType ?? CHARACTER_TYPE,
        })),
      },
    } satisfies EditBasicCourseRequest;

    await editCourseBase(newCourse);

    queryClient.invalidateQueries({
      queryKey: courseBaseByIdQueryOptions(queryClient, courseId).queryKey,
    });

    navigate({
      to: "/courses/$courseId",
      params: { courseId: courseId || "" },
    });
  }, [courseId, watch("selectedAvatars")]);

  if (courseDetailsLoading || !isAvatarsLoaded) return <LoadingScreen />;

  return allAvatars?.totalCount ? (
    <PageMainContent className="pt-0 pb-20 lg:pb-27.5">
      <FormHeading heading="Action.EditCourseAvatars" />

      <div className="sticky top-0 z-5">
        <div className="bg-background pt-3">
          <AvatarLegend />
        </div>
        <div className="from-background h-3 bg-linear-to-b from-50% to-transparent to-100%" />
      </div>

      {/* Selected Avatars */}
      <div className="my-6.25">
        <FieldLabel>
          {t("Courses.CurrentAvatars")} {tNum(selectedCount)}/{tNum(avatars_per_course)}
        </FieldLabel>
      </div>

      <div className="mb-5 grid grid-cols-1 gap-x-15 gap-y-6.75 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
        {selectedAvatars?.map((avatar, i) =>
          avatar ? (
            <AvatarGridItem
              key={i}
              selected
              className=""
              hoverOverlay={
                <Button
                  size="small"
                  variant="outlined"
                  className="fs-16 m-auto"
                  onClick={() => remove(i)}
                >
                  {t("Action.Deselect")}
                </Button>
              }
              image={avatar.url}
              name={""}
            />
          ) : (
            <EmptyAvatarGridItem key={i} />
          ),
        )}
      </div>

      <div className="border-divider w-full border-t-3" />

      {/* Other Avatars to choose from*/}
      <div className="my-6.25">
        <FieldLabel>{t("Courses.AvatarsYouCanChoose")}:</FieldLabel>
      </div>

      <div
        className={cn(
          "grid grid-cols-1 gap-x-15 gap-y-6.75 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6",
          {
            "mb-22.5": isDirty && isValid,
          },
        )}
      >
        {otherAvatars?.map((avatar) => (
          <div key={avatar.id}>
            <AvatarGridItem
              hoverOverlay={
                <Button
                  className="fs-16 m-auto"
                  onClick={() => append(avatar)}
                  disabled={selectedCount >= avatars_per_course}
                >
                  {t("Action.Select")}
                </Button>
              }
              image={avatar.url}
              name={""}
            />
          </div>
        ))}
      </div>

      {/* Save button */}
      {isDirty && isValid && (
        <div className="fixed end-20 bottom-10 z-2">
          <Button variant="contained" size="large" className="px-12" onClick={saveChanges}>
            {t("Action.SaveChanges")}
          </Button>
        </div>
      )}
    </PageMainContent>
  ) : (
    <NoAvatarsFound />
  );
}
